import { memo } from 'react';
import { X } from 'lucide-react';
import { DEFAULT_PROFILE_COLOR_ID, isProfileColorId } from '@cgraph-dev/shared-types';

import { DEFAULT_PROFILE_THEME_ID } from '@/data/profileThemes';
import { COLORS } from '@/stores/theme/presets';

import { ActionButtons } from './action-buttons';
import { AvatarZone } from './avatar-zone';
import { BannerCanvas } from './banner-canvas';
import { ACCENT_THEMES, normalizeAccentThemeId } from './constants';
import { IdentitySection } from './identity-section';
import { Nameplate } from './nameplate';
import { CardShell } from './profile-card-shell';
import { PulseDots } from './pulse-dots';
import type { NewProfileCardProps } from './types';

import './profile-card.css';

const NOOP = (): void => {};

export const NewProfileCard = memo(function NewProfileCard({
  user,
  mode = 'popout',
  variant = 'full',
  onMessage = NOOP,
  onTip = NOOP,
  onAddFriend = NOOP,
  onReviewFriendRequest = NOOP,
  onViewProfile = NOOP,
  onClose,
  friendshipStatus = 'none',
  isFriendActionPending = false,
  className,
}: NewProfileCardProps) {
  const themeId = user.accentTheme ?? normalizeAccentThemeId(user.profile_theme) ?? DEFAULT_PROFILE_THEME_ID;
  const theme = ACCENT_THEMES[themeId] ?? ACCENT_THEMES[DEFAULT_PROFILE_THEME_ID];
  const requestedProfileColor = user.profileColor ?? user.profile_color;
  const profileColor = isProfileColorId(requestedProfileColor)
    ? requestedProfileColor
    : DEFAULT_PROFILE_COLOR_ID;
  const accentColor = COLORS[profileColor].primary;
  const isMini = variant === 'mini';
  const isPreview = mode === 'preview';
  const nameplateId = user.nameplateId ?? user.equipped_nameplate;
  const displayNameFont = user.displayNameFont ?? user.display_name_font;
  const displayNameEffect = user.displayNameEffect ?? user.display_name_effect;
  const displayNameColor = user.displayNameColor ?? user.display_name_color;
  const displayNameSecondaryColor =
    user.displayNameSecondaryColor ?? user.display_name_secondary_color;
  const cardBackgroundImage = isMini
    ? theme.miniProfileBackgroundImage
    : theme.profileBackgroundImage;
  const cardBackgroundAsset = isMini
    ? theme.assetManifest.miniProfileBackground
    : theme.assetManifest.profileBackground;
  const fallbackHeaderImage = isMini
    ? (theme.miniProfileBackgroundImage ?? theme.previewImage)
    : (theme.profileBackgroundImage ?? theme.previewImage);
  const bannerBackgroundImage = cardBackgroundImage ? undefined : fallbackHeaderImage;

  const initials = user.displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const tipEnabled = (user.pulseFilled ?? 0) > 0;
  const showCloseButton = variant === 'full' && mode === 'popout' && Boolean(onClose);

  return (
    <CardShell
      accentColor={accentColor}
      backgroundImage={cardBackgroundImage}
      backgroundAsset={cardBackgroundAsset}
      className={className}
      profileColor={profileColor}
      profileThemeId={themeId}
    >
      {showCloseButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-3 top-3 z-20 grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-black/45 text-white/70 shadow-lg backdrop-blur transition hover:border-white/20 hover:bg-black/65 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* Banner */}
      <BannerCanvas
        bannerType={user.bannerType ?? 'static'}
        accentColor={accentColor}
        bannerBackground={cardBackgroundImage ? 'transparent' : theme.banner}
        backgroundImage={bannerBackgroundImage}
        variant={variant}
        decorative={!cardBackgroundImage}
      />

      {/* Card body with theme surface tint */}
      <div
        className="cgraph-game-profile-surface relative overflow-visible"
        data-profile-card-layout="fixed-identity-skeleton"
        data-profile-theme-surface="normalized"
        data-profile-background-image={cardBackgroundImage ?? undefined}
        style={{
          background: cardBackgroundImage
            ? 'linear-gradient(180deg, rgba(8,9,15,0.34) 0%, rgba(8,9,15,0.64) 48%, rgba(8,9,15,0.9) 100%)'
            : `linear-gradient(180deg, ${theme.surface} 0%, transparent 55%), #08090f`,
        }}
      >
        {/* Avatar */}
        <AvatarZone
          avatarUrl={user.avatarUrl}
          displayName={user.displayName}
          initials={initials}
          isOnline={user.isOnline}
          energyRingTier={user.energyRingTier ?? 'dim'}
          accentColor={accentColor}
          avatarBorderId={user.avatarBorderId}
          variant={variant}
        />

        {/* Nameplate */}
        <Nameplate
          displayName={user.displayName}
          nameplateId={nameplateId}
          displayNameFont={displayNameFont}
          displayNameEffect={displayNameEffect}
          displayNameColor={displayNameColor}
          displayNameSecondaryColor={displayNameSecondaryColor}
        />

        {/* Identity (compact in mini) */}
        <IdentitySection
          title={user.equippedTitle?.name ?? null}
          titleColor={user.equippedTitle?.color}
          titleAnimationType={user.equippedTitle?.animation?.type}
          titleGradient={user.equippedTitle?.gradient}
          titleLottieUrl={user.equippedTitle?.lottieUrl}
          titleImageUrl={user.equippedTitle?.imageUrl}
          bio={user.bio ?? null}
          badges={user.profileBadges ?? []}
          accentColor={accentColor}
          compact={isMini}
        />

        {/* Divider + reputation dots */}
        <>
          {!isMini && (
            <div
              className="mx-[1.2rem] my-[0.85rem] h-px"
              style={{
                background: `linear-gradient(90deg, transparent 0%, ${theme.border} 20%, ${theme.border} 80%, transparent 100%)`,
              }}
            />
          )}

          <PulseDots
            filled={user.pulseFilled ?? 0}
            tier={user.pulseTier ?? 'newcomer'}
            score={user.pulse ?? 0}
            prefersReducedMotion={true}
            compact={isMini}
          />
        </>

        {/* Action buttons (hidden in preview mode) */}
        {!isPreview && (
          <ActionButtons
            onMessage={onMessage}
            onTip={onTip}
            onAddFriend={onAddFriend}
            onReviewFriendRequest={onReviewFriendRequest}
            onViewProfile={onViewProfile}
            accentColor={accentColor}
            tipEnabled={tipEnabled}
            friendshipStatus={friendshipStatus}
            isFriendActionPending={isFriendActionPending}
            compact={isMini}
          />
        )}
      </div>
    </CardShell>
  );
});
